import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  PERMISSIONS,
  getUserRole,
  getTenantId,
  getPrimaryFacilityId,
  hasPermission,
  isOwnerAdmin,
  isOperationsAdmin,
  isAgencyAdmin,
  isFacilityPlanner,
} from "../security/permissions";

export const ApplicantContext = createContext(null);

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeId = (value) => String(value ?? "").trim();

const isWorkerRecord = (record) => {
  const role = normalize(record?.role);
  const userType = normalize(record?.userType);
  const accountType = normalize(record?.accountType);

  return (
    role === "worker" ||
    userType === "worker" ||
    accountType === "worker"
  );
};

const isArchived = (record) =>
  record?.archived === true ||
  normalize(record?.status) === "archived" ||
  normalize(record?.applicationStatus) === "archived";

const isRejected = (record) =>
  normalize(record?.workerStatus) === "rejected" ||
  normalize(record?.applicationStatus) === "rejected" ||
  normalize(record?.status) === "rejected";

const isReadyWorker = (record) => {
  const status = normalize(record?.status);
  const workerStatus = normalize(record?.workerStatus);
  const onboardingStatus = normalize(record?.onboardingStatus);
  const complianceStatus = normalize(record?.complianceStatus);
  const operationalState = normalize(record?.workerOperationalState);

  return (
    workerStatus === "ready" ||
    operationalState === "ready" ||
    (
      status === "active" &&
      onboardingStatus === "approved" &&
      complianceStatus === "approved"
    )
  );
};

const getPipelineStage = (record) => {
  if (record?.source === "invite") {
    return "invited";
  }

  if (isRejected(record)) {
    return "rejected";
  }

  if (isReadyWorker(record)) {
    return "ready";
  }

  const registrationStatus = normalize(record?.registrationStatus);
  const onboardingStatus = normalize(record?.onboardingStatus);
  const complianceStatus = normalize(record?.complianceStatus);
  const workerStatus = normalize(record?.workerStatus);
  const status = normalize(record?.status);

  if (
    onboardingStatus === "submitted" ||
    onboardingStatus === "pending_review" ||
    complianceStatus === "pending_review" ||
    workerStatus === "under_review"
  ) {
    return "review_queue";
  }

  if (
    onboardingStatus === "missing_docs" ||
    complianceStatus === "missing_docs" ||
    onboardingStatus === "reupload_required"
  ) {
    return "action_required";
  }

  if (
    registrationStatus === "approved" ||
    onboardingStatus === "registered" ||
    onboardingStatus === "onboarding" ||
    onboardingStatus === "in_progress" ||
    workerStatus === "onboarding" ||
    status === "onboarding"
  ) {
    return "onboarding";
  }

  return "registered";
};

const sortByMostRecent = (rows) => {
  return [...rows].sort((a, b) => {
    const aTime =
      a?.updatedAt?.toMillis?.() ??
      a?.createdAt?.toMillis?.() ??
      0;

    const bTime =
      b?.updatedAt?.toMillis?.() ??
      b?.createdAt?.toMillis?.() ??
      0;

    return bTime - aTime;
  });
};

export default function ApplicantProvider({
  children,
  profile,
  selectedTenantId = "all",
  selectedFacilityId = "all",
}) {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplicantId, setSelectedApplicantId] = useState(null);

   /*
 * =========================================================
 * CANONICAL APPLICANT PROVIDER IDENTITY / AUTHORIZATION
 * =========================================================
 *
 * ApplicantProvider must not maintain its own role aliases,
 * tenant resolution, facility resolution, or permission
 * interpretation.
 */

const profileTenantId =
  getTenantId(profile);

const profileFacilityId =
  getPrimaryFacilityId(profile);

const profileRole =
  getUserRole(profile);

const isPlannerProfile =
  isFacilityPlanner(profile);

/*
 * Applicant-management authority is capability based.
 *
 * Owner / Operations / Agency classification remains
 * explicit for enterprise administration while permission
 * overrides continue to flow through hasPermission().
 */
const canManageApplicants =
  isOwnerAdmin(profile) ||
  isOperationsAdmin(profile) ||
  isAgencyAdmin(profile) ||
  hasPermission(
    profile,
    PERMISSIONS.CAN_MANAGE_USERS
  ) ||
  hasPermission(
    profile,
    PERMISSIONS.CAN_VIEW_ALL_FACILITIES
  );

/*
 * A Facility Planner may read the applicant pipeline only
 * through its authorized tenant/facility scope. Firestore
 * enforcement will be synchronized in the rules phase.
 */
const canReadApplicantPipeline =
  canManageApplicants ||
  isPlannerProfile;

  console.log("APPLICANT PROVIDER SCOPE", {
    uid: profile?.uid,
    email: profile?.email,
    role: profileRole,
    tenantId: profileTenantId,
    facilityId: profileFacilityId,
    isPlannerProfile,
    canManageApplicants,
    canReadApplicantPipeline,
  });

  useEffect(() => {
    if (!canReadApplicantPipeline) {
      setUsers([]);
      setUsersLoading(false);

      console.log(
        "ApplicantProvider users listener skipped — role not authorized",
        {
          email: profile?.email,
          role: profileRole,
        }
      );

      return undefined;
    }

    if (!profileTenantId) {
      setUsers([]);
      setUsersLoading(false);

      console.warn(
        "ApplicantProvider users listener skipped — tenantId missing",
        {
          email: profile?.email,
          role: profileRole,
        }
      );

      return undefined;
    }

    if (
      isPlannerProfile &&
      !profileFacilityId
    ) {
      setUsers([]);
      setUsersLoading(false);

      console.warn(
        "ApplicantProvider users listener skipped — Planner facilityId missing",
        {
          email: profile?.email,
          tenantId: profileTenantId,
        }
      );

      return undefined;
    }

    setUsersLoading(true);
    setError(null);

    const usersRef =
      collection(db, "users");

    /*
     * Admin / Operations:
     * tenant-wide workforce pipeline.
     *
     * Facility Planner:
     * tenant + authorized facility only.
     */
    const usersQuery =
      isPlannerProfile
        ? query(
            usersRef,
            where(
              "tenantId",
              "==",
              profileTenantId
            ),
            where(
              "facilityId",
              "==",
              profileFacilityId
            )
          )
        : query(
            usersRef,
            where(
              "tenantId",
              "==",
              profileTenantId
            )
          );

    console.log(
      "ApplicantProvider users listener start",
      {
        email: profile?.email,
        role: profileRole,
        tenantId:
          profileTenantId,
        facilityId:
          isPlannerProfile
            ? profileFacilityId
            : "ALL AUTHORIZED",
      }
    );

    const unsubscribe =
      onSnapshot(
        usersQuery,
        (snapshot) => {
          const rows =
            snapshot.docs
              .map((document) => ({
                uid: document.id,
                id: document.id,
                source: "user",
                ...document.data(),
              }))
              .filter(
                isWorkerRecord
              )
              .filter((record) => {
                const recordTenant =
                  normalize(
                    record?.tenantId
                  );

                if (
                  recordTenant !==
                  profileTenantId
                ) {
                  return false;
                }

                if (
                  !isPlannerProfile
                ) {
                  return true;
                }

                return (
                  normalize(
                    record?.facilityId
                  ) ===
                  profileFacilityId
                );
              });

          console.log(
            "ApplicantProvider users listener success",
            {
              tenantId:
                profileTenantId,
              facilityId:
                isPlannerProfile
                  ? profileFacilityId
                  : "ALL AUTHORIZED",
              size: rows.length,
            }
          );

          setUsers(rows);
          setUsersLoading(false);
        },
        (snapshotError) => {
          console.error(
            "ApplicantProvider users listener failed:",
            {
              email:
                profile?.email,
              role:
                profileRole,
              tenantId:
                profileTenantId,
              facilityId:
                profileFacilityId,
              code:
                snapshotError?.code,
              message:
                snapshotError?.message,
              error:
                snapshotError,
            }
          );

          setUsers([]);
          setError(snapshotError);
          setUsersLoading(false);
        }
      );

    return () => {
      console.log(
        "ApplicantProvider users listener stop",
        {
          tenantId:
            profileTenantId,
          facilityId:
            profileFacilityId,
        }
      );

      unsubscribe();
    };
  }, [
    profileTenantId,
    profileFacilityId,
    profileRole,
    isPlannerProfile,
    canReadApplicantPipeline,
    profile?.email,
  ]);

  useEffect(() => {
    if (!canReadApplicantPipeline) {
      setInvites([]);
      setInvitesLoading(false);

      console.log(
        "ApplicantProvider invites listener skipped — role not authorized",
        {
          email: profile?.email,
          role: profileRole,
        }
      );

      return undefined;
    }

    if (!profileTenantId) {
      setInvites([]);
      setInvitesLoading(false);

      console.warn(
        "ApplicantProvider invites listener skipped — tenantId missing",
        {
          email: profile?.email,
          role: profileRole,
        }
      );

      return undefined;
    }

    if (
      isPlannerProfile &&
      !profileFacilityId
    ) {
      setInvites([]);
      setInvitesLoading(false);

      console.warn(
        "ApplicantProvider invites listener skipped — Planner facilityId missing",
        {
          email: profile?.email,
          tenantId: profileTenantId,
        }
      );

      return undefined;
    }

    setInvitesLoading(true);
    setError(null);

    const invitesRef =
      collection(db, "invites");

    /*
     * Admin / Operations:
     * tenant-wide invitation pipeline.
     *
     * Facility Planner:
     * tenant + authorized facility only.
     */
    const invitesQuery =
      isPlannerProfile
        ? query(
            invitesRef,
            where(
              "tenantId",
              "==",
              profileTenantId
            ),
            where(
              "facilityId",
              "==",
              profileFacilityId
            )
          )
        : query(
            invitesRef,
            where(
              "tenantId",
              "==",
              profileTenantId
            )
          );

    console.log(
      "ApplicantProvider invites listener start",
      {
        email: profile?.email,
        role: profileRole,
        tenantId:
          profileTenantId,
        facilityId:
          isPlannerProfile
            ? profileFacilityId
            : "ALL AUTHORIZED",
      }
    );

    const unsubscribe =
      onSnapshot(
        invitesQuery,
        (snapshot) => {
          const rows =
            snapshot.docs
              .map((document) => ({
                inviteId:
                  document.id,
                id: document.id,
                source: "invite",
                ...document.data(),
              }))
              .filter((record) => {
                const recordTenant =
                  normalize(
                    record?.tenantId
                  );

                if (
                  recordTenant !==
                  profileTenantId
                ) {
                  return false;
                }

                if (
                  !isPlannerProfile
                ) {
                  return true;
                }

                return (
                  normalize(
                    record?.facilityId
                  ) ===
                  profileFacilityId
                );
              });

          console.log(
            "ApplicantProvider invites listener success",
            {
              tenantId:
                profileTenantId,
              facilityId:
                isPlannerProfile
                  ? profileFacilityId
                  : "ALL AUTHORIZED",
              size: rows.length,
            }
          );

          setInvites(rows);
          setInvitesLoading(false);
        },
        (snapshotError) => {
          console.error(
            "ApplicantProvider invites listener failed:",
            {
              email:
                profile?.email,
              role:
                profileRole,
              tenantId:
                profileTenantId,
              facilityId:
                profileFacilityId,
              code:
                snapshotError?.code,
              message:
                snapshotError?.message,
              error:
                snapshotError,
            }
          );

          setInvites([]);
          setError(snapshotError);
          setInvitesLoading(false);
        }
      );

    return () => {
      console.log(
        "ApplicantProvider invites listener stop",
        {
          tenantId:
            profileTenantId,
          facilityId:
            profileFacilityId,
        }
      );

      unsubscribe();
    };
  }, [
    profileTenantId,
    profileFacilityId,
    profileRole,
    isPlannerProfile,
    canReadApplicantPipeline,
    profile?.email,
  ]);

  const applicants = useMemo(() => {
    const selectedTenant = normalizeId(selectedTenantId);
    const selectedFacility = normalizeId(selectedFacilityId);

    const activeUsers = users
      .filter((record) => !isArchived(record))
      .filter((record) => {
        if (!selectedTenant || selectedTenant === "all") {
          return true;
        }

        return normalizeId(record?.tenantId) === selectedTenant;
      })
      .filter((record) => {
        if (!selectedFacility || selectedFacility === "all") {
          return true;
        }

        const primaryFacilityId = normalizeId(
          record?.primaryFacilityId || record?.facilityId
        );

        const facilityIds = [
          ...(Array.isArray(record?.facilityIds)
            ? record.facilityIds
            : []),
          ...(Array.isArray(record?.allowedFacilityIds)
            ? record.allowedFacilityIds
            : []),
        ].map(normalizeId);

        return (
          primaryFacilityId === selectedFacility ||
          facilityIds.includes(selectedFacility)
        );
      })
      .map((record) => ({
        ...record,
        pipelineStage: getPipelineStage(record),
      }));

    const activeInvites = invites
      .filter((record) => !isArchived(record))
      .filter((record) => {
        const inviteStatus = normalize(record?.status);

        return ![
          "accepted",
          "registered",
          "completed",
          "cancelled",
          "rejected",
        ].includes(inviteStatus);
      })
      .filter((record) => {
        if (!selectedTenant || selectedTenant === "all") {
          return true;
        }

        return normalizeId(record?.tenantId) === selectedTenant;
      })
      .filter((record) => {
        if (!selectedFacility || selectedFacility === "all") {
          return true;
        }

        const primaryFacilityId = normalizeId(
          record?.primaryFacilityId || record?.facilityId
        );

        const facilityIds = Array.isArray(record?.facilityIds)
          ? record.facilityIds.map(normalizeId)
          : [];

        return (
          primaryFacilityId === selectedFacility ||
          facilityIds.includes(selectedFacility)
        );
      })
      .map((record) => ({
        ...record,
        pipelineStage: "invited",
      }));

    return sortByMostRecent([...activeInvites, ...activeUsers]);
  }, [
    users,
    invites,
    selectedTenantId,
    selectedFacilityId,
  ]);

  const applicantById = useMemo(() => {
    const map = new Map();

    applicants.forEach((record) => {
      const key = normalizeId(
        record?.uid || record?.inviteId || record?.id
      );

      if (key) {
        map.set(key, record);
      }
    });

    return map;
  }, [applicants]);

  const selectedApplicant = useMemo(() => {
    if (!selectedApplicantId) {
      return null;
    }

    return applicantById.get(normalizeId(selectedApplicantId)) || null;
  }, [applicantById, selectedApplicantId]);

  useEffect(() => {
    if (
      selectedApplicantId &&
      !applicantById.has(normalizeId(selectedApplicantId))
    ) {
      setSelectedApplicantId(null);
    }
  }, [applicantById, selectedApplicantId]);

  const invitedApplicants = useMemo(
    () =>
      applicants.filter(
        (record) => record.pipelineStage === "invited"
      ),
    [applicants]
  );

  const registeredApplicants = useMemo(
    () =>
      applicants.filter(
        (record) => record.pipelineStage === "registered"
      ),
    [applicants]
  );

  const onboardingApplicants = useMemo(
    () =>
      applicants.filter(
        (record) => record.pipelineStage === "onboarding"
      ),
    [applicants]
  );

  const reviewQueue = useMemo(
    () =>
      applicants.filter(
        (record) => record.pipelineStage === "review_queue"
      ),
    [applicants]
  );

  const actionRequiredApplicants = useMemo(
    () =>
      applicants.filter(
        (record) => record.pipelineStage === "action_required"
      ),
    [applicants]
  );

  const readyApplicants = useMemo(
    () =>
      applicants.filter(
        (record) => record.pipelineStage === "ready"
      ),
    [applicants]
  );

  const rejectedApplicants = useMemo(
    () =>
      applicants.filter(
        (record) => record.pipelineStage === "rejected"
      ),
    [applicants]
  );

  const pipelineCounts = useMemo(
    () => ({
      total: applicants.length,
      invited: invitedApplicants.length,
      registered: registeredApplicants.length,
      onboarding: onboardingApplicants.length,
      reviewQueue: reviewQueue.length,
      actionRequired: actionRequiredApplicants.length,
      ready: readyApplicants.length,
      rejected: rejectedApplicants.length,
    }),
    [
      applicants.length,
      invitedApplicants.length,
      registeredApplicants.length,
      onboardingApplicants.length,
      reviewQueue.length,
      actionRequiredApplicants.length,
      readyApplicants.length,
      rejectedApplicants.length,
    ]
  );

  const selectApplicant = useCallback((recordOrId) => {
    const id =
      typeof recordOrId === "object"
        ? recordOrId?.uid ||
          recordOrId?.inviteId ||
          recordOrId?.id
        : recordOrId;

    setSelectedApplicantId(normalizeId(id) || null);
  }, []);

  const closeApplicant = useCallback(() => {
    setSelectedApplicantId(null);
  }, []);

  const value = useMemo(
    () => ({
      applicants,
      users,
      invites,

      loading: usersLoading || invitesLoading,
      error,

      selectedApplicantId,
      selectedApplicant,
      selectApplicant,
      closeApplicant,
      setSelectedApplicantId,

      invitedApplicants,
      registeredApplicants,
      onboardingApplicants,
      reviewQueue,
      actionRequiredApplicants,
      readyApplicants,
      rejectedApplicants,

      workforce: readyApplicants,
      pipelineCounts,
    }),
    [
      applicants,
      users,
      invites,
      usersLoading,
      invitesLoading,
      error,
      selectedApplicantId,
      selectedApplicant,
      selectApplicant,
      closeApplicant,
      invitedApplicants,
      registeredApplicants,
      onboardingApplicants,
      reviewQueue,
      actionRequiredApplicants,
      readyApplicants,
      rejectedApplicants,
      pipelineCounts,
    ]
  );

  return (
    <ApplicantContext.Provider value={value}>
      {children}
    </ApplicantContext.Provider>
  );
}